import { type MetaFunction } from 'react-router'
import MixtilesSans400 from 'media/fonts/MixtilesSans-400Regular.woff'
import MixtilesSans500 from 'media/fonts/MixtilesSans-500Medium.woff'
import MixtilesSans700 from 'media/fonts/MixtilesSans-700Bold.woff'
import MixtilesSans900 from 'media/fonts/MixtilesSans-900Black.woff'
import { viewportMeta } from 'utils/meta'
import { type RootLoaderType } from './loader.server'
import { FAVICON_LINKS } from './faviconLinks'
import { DEFAULT_THEME_COLOR } from '../../components/ThemeColor/BrowserThemeColor'
import { DEFAULT_SITE_DESCRIPTION, DEFAULT_SITE_TITLE } from '../utils/consts'
import {
  isLocaleBasedRoute,
  getAlternateLocaleLinks,
} from '../utils/localeUtils'
import { getOrigin } from '../../utils/UrlUtils'
import { type AlternateLink } from './types'
import {
  getDescriptionMetaTags,
  getImageMetaTags,
  getTitleMetaTags,
} from 'app/utils/meta'

export const meta: MetaFunction<RootLoaderType> = ({
  location,
  loaderData: rootLoaderData,
}) => {
  let title = DEFAULT_SITE_TITLE
  let description = DEFAULT_SITE_DESCRIPTION
  let url = 'https://www.mixtiles.com'
  let siteName = 'Mixtiles'

  if (location.pathname.startsWith('/collection/home')) {
    title = 'Mixtiles - Decorate Your Walls in Minutes with Stunning Wall Art'
  }

  const loaderData =
    rootLoaderData && !('error' in rootLoaderData) ? rootLoaderData : undefined

  const robotsContent =
    loaderData?.keys.runtimeEnv === 'production' ? 'all' : 'noindex, nofollow'

  const defaultAlternateLinks: AlternateLink[] = [
    {
      tagName: 'link',
      rel: 'alternate' as const,
      hrefLang: 'en',
      href: 'https://www.mixtiles.com',
    },
    {
      tagName: 'link',
      rel: 'alternate' as const,
      hrefLang: 'de',
      href: 'https://www.mixtiles.de',
    },
  ]

  const pathname = location.pathname
  const origin = loaderData?.fullUrl
    ? new URL(loaderData.fullUrl).origin
    : getOrigin()

  const alternateLinks: AlternateLink[] = isLocaleBasedRoute(pathname)
    ? getAlternateLocaleLinks(pathname, origin)
    : defaultAlternateLinks

  return [
    ...getTitleMetaTags(title),
    ...getDescriptionMetaTags(description),
    ...getImageMetaTags('https://www.mixtiles.com/preview3.jpg'),
    {
      name: 'viewport',
      content: viewportMeta({ interactiveWidget: 'resizes-visual' }),
    },
    { charset: 'utf-8' },
    { name: 'theme-color', content: DEFAULT_THEME_COLOR },
    { name: 'color-scheme', content: 'light' },
    { name: 'msapplication-TileColor', content: '#da532c' },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: url },
    { property: 'og:site_name', content: siteName },
    { property: 'og:email', content: 'hi@mixtiles.com' },
    { property: 'fb:page_id', content: 'mixtiles' },
    { property: 'twitter:card', content: 'summary' },
    { property: 'twitter:url', content: url },
    { name: 'p:domain_verify', content: '41cc253422097f4fa383e71ff613812c' },
    {
      name: 'robots',
      content: robotsContent,
    },
    // Static links that are always included
    /* <!--
        manifest.json provides metadata used when your web app is added to the
        homescreen on Android. See https://developers.google.com/web/fundamentals/engage-and-retain/web-app-manifest/
      --> */
    {
      tagName: 'link',
      rel: 'manifest',
      href: '/manifest.json',
    },
    ...FAVICON_LINKS,
    {
      tagName: 'link',
      rel: 'preload',
      as: 'font',
      href: MixtilesSans400,
      type: 'font/woff2',
      crossOrigin: 'anonymous',
    },
    {
      tagName: 'link',
      rel: 'preload',
      as: 'font',
      href: MixtilesSans500,
      type: 'font/woff2',
      crossOrigin: 'anonymous',
    },
    {
      tagName: 'link',
      rel: 'preload',
      as: 'font',
      href: MixtilesSans700,
      type: 'font/woff2',
      crossOrigin: 'anonymous',
    },
    {
      tagName: 'link',
      rel: 'preload',
      as: 'font',
      href: MixtilesSans900,
      type: 'font/woff2',
      crossOrigin: 'anonymous',
    },
    {
      tagName: 'link',
      rel: 'preconnect',
      href: '//privacy-proxy.usercentrics.eu',
    },
    {
      tagName: 'link',
      rel: 'preload',
      href: '//privacy-proxy.usercentrics.eu/latest/uc-block.bundle.js',
      as: 'script',
    },
    // Add alternate links as meta tags
    ...alternateLinks,
  ]
}
